// OpenAI service for streaming content generation
// Based on implementation plan Phase 5.1

import { aiAxiosInstance } from '../../../utils/axiosInstance';
import { 
  GenerationParams, 
  StreamingCallbacks, 
  ContentGenerationRequest,
  GeneratedStory
} from '../types/streaming.types';
import { 
  buildStoryPrompt, 
  buildIntroductionActivitiesPrompt, 
  buildPracticeActivitiesPrompt,
  parseStoryContent,
  calculateStoryMetadata
} from '../utils/promptTemplates';
import { Story } from '../types/lessonPlan.types';

// Interface for prompt template parameters
interface PromptParams {
  lexileLevel: string;
  genre: string;
  topic: string;
  paragraphCount: number;
  standard: string;
  storyTitle?: string;
}

/**
 * OpenAI Service for generating lesson content with streaming support
 */
export class OpenAIService {
  private readonly baseURL: string;
  private readonly maxRetries: number = 3;
  private readonly retryDelay: number = 1000; // milliseconds

  constructor() {
    // Use production AI backend URL if not in development
    const isDevelopment = process.env.NODE_ENV === 'development';
    this.baseURL = process.env.REACT_APP_AI_API_URL || 
      (isDevelopment ? 'http://localhost:5001' : 'https://teach-ai-aq9x.vercel.app');
  }

  async translateSection(
    params: {
      storyContent?: string;
      activitiesContent?: string;
      targetLanguage: string;
      genre?: string;
    },
    callbacks: StreamingCallbacks
  ): Promise<{ story?: string; activities?: string; storyTitle?: string }> {
    try {
      const response = await fetch(`${this.baseURL}/small-group/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      if (!response.body) throw new Error('No response body available for streaming');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      // Progress buffer only for UI progress updates; not used for final values
      let progressBuffer = '';
      let translatedStory: string | undefined;
      let translatedActivities: string | undefined;
      let translatedStoryTitle: string | undefined;
      callbacks.onStart?.();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n').filter((l) => l.trim());
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          try {
            const parsed = JSON.parse(data);
            if (parsed.content) {
              // Accumulate for progress only; final values will come from structured fields on 'done'
              const part = typeof parsed.content === 'string' ? parsed.content : JSON.stringify(parsed.content);
              progressBuffer += part;
              callbacks.onProgress?.(progressBuffer, 0);
            }
            if (parsed.done) {
              // prefer structured fields if present
              if (typeof parsed.story === 'string') translatedStory = parsed.story;
              if (typeof parsed.activities === 'string') translatedActivities = parsed.activities;
              if (typeof parsed.storyTitle === 'string') translatedStoryTitle = parsed.storyTitle;
              console.log('[Translation] Received translated storyTitle:', translatedStoryTitle || 'NOT PROVIDED');
              callbacks.onComplete?.(`${translatedStory}\n${translatedActivities}`.trim());
              return { story: translatedStory || undefined, activities: translatedActivities || undefined, storyTitle: translatedStoryTitle };
            }
          } catch (_e) {
            // ignore non-JSON lines
          }
        }
      }
      // fallback: if no structured fields were returned, treat progress buffer as story
      callbacks.onComplete?.(progressBuffer);
      return { story: progressBuffer || undefined, activities: translatedActivities || undefined, storyTitle: translatedStoryTitle };
    } catch (error) {
      console.error('Error translating section with real API:', error);
      callbacks.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Generate a story with streaming updates
   * @param params Generation parameters including lexile level, genre, topic, etc.
   * @param callbacks Streaming callbacks for real-time updates
   * @param signal Optional AbortSignal for cancelling the request
   * @returns Promise resolving to the complete story object
   */
  async generateStory(params: GenerationParams, callbacks: StreamingCallbacks, signal?: AbortSignal): Promise<Story> {
    const contentLength = params.contentLength || params.paragraphCount || 3;
    const maxTokens = this.calculateMaxTokens(contentLength);
    
    // Use real API
    try {
      const requestBody = {
        subject: params.subject || 'ELA', // NEW: Include subject for routing
        standardId: params.standardId,
        standard: params.standard, // Include full standard details
        // Legacy ELA fields
        lexileLevel: params.lexileLevel || '',
        genre: params.genre || '',
        paragraphCount: params.paragraphCount || 3,
        // NEW: Generic fields (will be used by Math)
        difficulty: params.difficulty || params.lexileLevel || '',
        contentType: params.contentType || params.genre || '',
        contentLength: params.contentLength || params.paragraphCount || 3,
        // Common fields
        topic: params.topic,
        independentPracticeTopic: params.independentPracticeTopic,
        section: params.section,
        language: params.language,
        revisingAndEditing: params.revisingAndEditing
      };

      const response = await fetch(`${this.baseURL}/small-group/generate-story`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('No response body available for streaming');
      }

      const content = await this.processStream(response.body, callbacks, signal);
      const { title, content: storyContent } = parseStoryContent(content);
      const metadata = calculateStoryMetadata(storyContent);
      
      return {
        id: `story-${Date.now()}`,
        title,
        content: storyContent,
        metadata: {
          id: `metadata-${Date.now()}`,
          title,
          wordCount: metadata.wordCount,
          readingTimeMinutes: metadata.readingTimeMinutes,
          createdAt: new Date()
        }
      };
    } catch (error) {
      console.error('Error generating story with real API:', error);
      throw error;
    }
  }

  /**
   * Generate introduction activities with streaming updates
   * @param params Generation parameters
   * @param callbacks Streaming callbacks for real-time updates
   * @param signal Optional AbortSignal for cancelling the request
   * @returns Promise resolving to the complete activities content
   */
  async generateIntroductionActivities(
    params: GenerationParams, 
    callbacks: StreamingCallbacks,
    signal?: AbortSignal
  ): Promise<string> {
    try {
      const requestBody = {
        subject: params.subject || 'ELA', // NEW: Include subject for routing
        standardId: params.standardId,
        standard: params.standard, // Include full standard details
        // Legacy ELA fields
        lexileLevel: params.lexileLevel || '',
        genre: params.genre || '',
        paragraphCount: params.paragraphCount || 3,
        // NEW: Generic fields (will be used by Math)
        difficulty: params.difficulty || params.lexileLevel || '',
        contentType: params.contentType || params.genre || '',
        contentLength: params.contentLength || params.paragraphCount || 3,
        // Common fields
        topic: params.topic,
        independentPracticeTopic: params.independentPracticeTopic,
        storyTitle: params.storyTitle,
        storyContent: params.storyContent, // NEW: Pass instructional content for alignment
        activitiesType: 'introduction',
        language: params.language,
        revisingAndEditing: params.revisingAndEditing
      };

      const response = await fetch(`${this.baseURL}/small-group/generate-activities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('No response body available for streaming');
      }

      return await this.processStream(response.body, callbacks, signal);
    } catch (error) {
      console.error('Error generating introduction activities with real API:', error);
      throw error;
    }
  }

  /**
   * Generate practice activities with streaming updates
   * @param params Generation parameters
   * @param callbacks Streaming callbacks for real-time updates
   * @param signal Optional AbortSignal for cancelling the request
   * @returns Promise resolving to the complete activities content
   */
  async generatePracticeActivities(
    params: GenerationParams, 
    callbacks: StreamingCallbacks,
    signal?: AbortSignal
  ): Promise<string> {
    try {
      const requestBody = {
        subject: params.subject || 'ELA', // NEW: Include subject for routing
        standardId: params.standardId,
        standard: params.standard, // Include full standard details
        // Legacy ELA fields
        lexileLevel: params.lexileLevel || '',
        genre: params.genre || '',
        paragraphCount: params.paragraphCount || 3,
        // NEW: Generic fields (will be used by Math)
        difficulty: params.difficulty || params.lexileLevel || '',
        contentType: params.contentType || params.genre || '',
        contentLength: params.contentLength || params.paragraphCount || 3,
        // Common fields
        topic: params.topic,
        independentPracticeTopic: params.independentPracticeTopic,
        storyTitle: params.storyTitle,
        storyContent: params.storyContent, // NEW: Pass instructional content for alignment
        activitiesType: 'practice',
        language: params.language,
        revisingAndEditing: params.revisingAndEditing
      };

      const response = await fetch(`${this.baseURL}/small-group/generate-activities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('No response body available for streaming');
      }

      return await this.processStream(response.body, callbacks, signal);
    } catch (error) {
      console.error('Error generating practice activities with real API:', error);
      throw error;
    }
  }

  /**
   * Stream completion from OpenAI API with retry logic
   * @param prompt The prompt to send to the AI
   * @param callbacks Streaming callbacks
   * @param maxTokens Maximum tokens for the response
   * @returns Promise resolving to the complete generated content
   */
  private async streamCompletion(
    prompt: string,
    callbacks: StreamingCallbacks,
    maxTokens: number = 800
  ): Promise<string> {
    let lastAttempt = 1;
    
    for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
      try {
        lastAttempt = attempt;
        
        const response = await fetch(this.getStreamEndpoint(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prompt,
            maxTokens,
            temperature: 0.7,
            stream: true
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response body available for streaming');
        }

        return await this.processStream(response.body, callbacks);
      } catch (error) {
        console.log(`Stream attempt ${attempt} failed:`, error);
        
        if (attempt === this.maxRetries) {
          throw new Error(`Failed to generate content after ${this.maxRetries} attempts: ${error}`);
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, this.retryDelay * attempt));
      }
    }
    
    throw new Error(`Failed after ${lastAttempt} attempts`);
  }

  /**
   * Calculate appropriate max tokens based on paragraph count
   * @param paragraphCount Number of paragraphs requested
   * @returns Calculated maximum tokens
   */
  private calculateMaxTokens(paragraphCount: number): number {
    return Math.max(300, paragraphCount * 150); // Base 300 + 150 per paragraph
  }

  /**
   * Get the streaming endpoint URL
   * @returns The complete endpoint URL for streaming
   */
  private getStreamEndpoint(): string {
    return `${this.baseURL}/stream`;
  }

  private async processStream(
    body: ReadableStream,
    callbacks: StreamingCallbacks,
    signal?: AbortSignal
  ): Promise<string> {
    let fullContent = '';
    let contentBuffer = ''; // Buffer to accumulate content chunks
    let estimatedTotal = 1000; // Rough estimate for progress calculation
    const reader = body.getReader();

    try {
      const decoder = new TextDecoder();
      callbacks.onStart?.();

      while (true) {
        // Check if request was aborted
        if (signal?.aborted) {
          reader.cancel();
          throw new Error('Request cancelled by user');
        }
        
        const { done, value } = await reader.read();
        
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n').filter(line => line.trim());

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            
            if (data === '[DONE]') {
              // Send final buffered content
              if (contentBuffer.trim()) {
                fullContent += contentBuffer;
                callbacks.onProgress?.(fullContent, 100);
              }
              callbacks.onComplete?.(fullContent);
              return fullContent;
            }

            try {
              const parsed = JSON.parse(data);
              // Log the exact parsed data received from the backend
              console.log('➡️ Backend sent data:', JSON.stringify(parsed));
              
              // Handle our backend format: {"content": "text"} or {"done": true}
              if (parsed.done) {
                // Send final buffered content
                if (contentBuffer.trim()) {
                  fullContent += contentBuffer;
                  callbacks.onProgress?.(fullContent, 100);
                }
                callbacks.onComplete?.(fullContent);
                return fullContent;
              }
              
              if (parsed.content) {
                // CRITICAL FIX: Ensure content is always a string to prevent {object Object} rendering
                const contentChunk = typeof parsed.content === 'string' 
                  ? parsed.content 
                  : JSON.stringify(parsed.content);
                
                // Add to buffer instead of directly to fullContent
                contentBuffer += contentChunk;
                
                // Debug logging
                console.log('📝 Received chunk:', parsed.content);
                console.log('📝 Processed chunk (ensuring string):', contentChunk);
                console.log('📦 Buffer now:', contentBuffer);
                console.log('📚 Full content before adding current buffer:', fullContent);
                
                // Only send content updates when we have complete words/sentences
                // This prevents showing partial/broken text
                if (this.shouldFlushBuffer(contentBuffer)) {
                  fullContent += contentBuffer;
                  
                  // Calculate progress (rough estimate)
                  const progress = Math.min(95, (fullContent.length / estimatedTotal) * 100);
                  
                  console.log('🚀 Flushing buffer to UI. Full content now:', fullContent);
                  console.log('✨ Buffer cleared. Buffer is now:', contentBuffer);
                  
                  // Send the FULL accumulated content, not just the buffer
                  callbacks.onProgress?.(fullContent, progress);
                  
                  // Clear buffer after flushing
                  contentBuffer = '';
                }
              }
            } catch (parseError) {
              console.warn('Failed to parse streaming response:', parseError, 'Raw data:', data);
            }
          }
        }
      }

      // Send any remaining buffered content
      if (contentBuffer.trim()) {
        fullContent += contentBuffer;
        callbacks.onProgress?.(fullContent, 100);
      }

      callbacks.onComplete?.(fullContent);
    } catch (error) {
      console.error('Stream processing error:', error);
      throw error;
    } finally {
      reader.releaseLock();
    }

    return fullContent;
  }

  /**
   * Determine if the content buffer should be flushed to the UI
   * Flush on word boundaries for smooth word-by-word streaming
   */
  private shouldFlushBuffer(buffer: string): boolean {
    // Don't flush empty buffers
    if (!buffer.trim()) return false;
    
    // Flush if buffer is getting too long (safety measure)
    if (buffer.length > 150) return true;
    
    // Flush when we have complete words (ends with space)
    // This enables word-by-word streaming
    if (buffer.endsWith(' ') && buffer.trim().length > 0) return true;
    
    // Flush when we have complete sentences (ends with punctuation + space)
    if (/[.!?]\s+/.test(buffer)) return true;
    
    // Flush when we have complete lines (ends with newline)
    if (buffer.includes('\n')) return true;
    
    // Flush when we have punctuation followed by text (for better sentence flow)
    if (/[.!?]\w/.test(buffer)) return true;
    
    return false;
  }
}

// Export singleton instance
export const openaiService = new OpenAIService(); 